/**
 * Live read/write layer for the CSR Request Log — the shared
 * `mfx_estimating_suite/requests` doc (`{ items: CsrRequest[] }`) and the
 * `meta.reqSeq` counter for REQ-#### ids. Same source of truth the CSR app
 * and the morning digests use, so the unified app stays in sync.
 */
import { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

const SUITE = 'mfx_estimating_suite';

export interface ReqHistory { ts: string; who?: string; act: string; }
export interface CsrRequest {
  id: string;
  date?: string; client?: string; contact?: string; method?: string;
  types?: string[]; desc: string; nextAction?: string;
  loggedBy?: string; assignee?: string; priority?: string; status?: string;
  due?: string; runDate?: string; ref?: string; notes?: string;
  closedDate?: string; closedBy?: string; created?: string;
  history?: ReqHistory[];
}

export const REQ_STATUSES = ['New', 'In Progress', 'On Schedule — Not Running', 'Waiting on Client', 'Samples Sent', 'Quoted', 'On Hold', 'Completed', 'Cancelled'];
export const REQ_METHODS = ['Call', 'Email', 'Text', 'In-person', 'Web/Portal', 'Other'];
export const REQ_TYPES = ['Sample', 'Quote', 'Dieline', 'Artwork/Proof', 'Reorder', 'Reorder w/ Change', 'Pricing', 'New Project', 'Question', 'Complaint', 'Other'];
export const PRIORITIES = ['Low', 'Normal', 'High', 'Urgent'];

export const isRequestOpen = (s?: string) => !!s && s !== 'Completed' && s !== 'Cancelled';
export const isRequestClosed = (s?: string) => s === 'Completed' || s === 'Cancelled';

export interface RequestsState { requests: CsrRequest[]; reqSeq: number; loading: boolean; live: boolean; error: string | null; }

export function useRequests(): RequestsState {
  const [state, setState] = useState<RequestsState>({ requests: [], reqSeq: 1, loading: true, live: false, error: null });
  useEffect(() => {
    let alive = true;
    getDoc(doc(db, SUITE, 'meta'))
      .then((m) => { if (alive && m.exists()) { const seq = (m.data() as { reqSeq?: number }).reqSeq; if (seq) setState((s) => ({ ...s, reqSeq: seq })); } })
      .catch(() => {});
    const unsub = onSnapshot(doc(db, SUITE, 'requests'),
      (snap) => {
        if (!alive) return;
        const data = snap.exists() ? (snap.data() as { items?: CsrRequest[] }) : {};
        const items = Array.isArray(data.items) ? data.items : [];
        setState((s) => ({ ...s, requests: items, loading: false, live: true, error: null }));
      },
      (e) => { if (alive) setState((s) => ({ ...s, loading: false, live: false, error: e?.message || 'Could not load requests' })); });
    return () => { alive = false; unsub(); };
  }, []);
  return state;
}

export async function saveRequests(items: CsrRequest[]): Promise<void> {
  await setDoc(doc(db, SUITE, 'requests'), { items });
}

/** Reserve the next REQ-#### id and bump meta.reqSeq. */
export async function nextRequestId(): Promise<string> {
  const m = await getDoc(doc(db, SUITE, 'meta'));
  const seq = (m.exists() ? (m.data() as { reqSeq?: number }).reqSeq : 0) || 0;
  const next = seq + 1;
  await setDoc(doc(db, SUITE, 'meta'), { reqSeq: next + 1 }, { merge: true });
  return 'REQ-' + String(next).padStart(4, '0');
}

export function nowTs(): string { return new Date().toISOString(); }

export function todayPacific(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}
export function lastActivity(r: CsrRequest): string {
  const h = r.history || [];
  return h.reduce((m, x) => (x.ts && x.ts > m ? x.ts : m), r.created || r.date || '');
}
